import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { API_ENDPOINT } from '../../constants';
import { fetchWrapper } from '../../helpers';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { ITreatmentPlanSymptom } from '../../utils/types';

const name = 'symptoms';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<SymptomsState>) => createExtraReducers(builder),
});

export interface SymptomsState {
  list: Array<ITreatmentPlanSymptom>;
  loading: boolean;
  error: any;
}

function createInitialState(): SymptomsState {
  return {
    list: [],
    loading: false,
    error: null,
  };
}

function createReducers() {
  return {};
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/treatment_plans`;

  return {
    getSymptoms: getSymptoms(),
    createSymptom: createSymptom(),
    updateSymptom: updateSymptom(),
    removeSymptom: removeSymptom(),
  };

  function getSymptoms() {
    return createAsyncThunk(
      `${name}/getSymptoms`,
      async ({ treatmentPlanId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${treatmentPlanId}/symptoms/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createSymptom() {
    return createAsyncThunk(
      `${name}/createSymptom`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${treatmentPlanId}/symptoms/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function updateSymptom() {
    return createAsyncThunk(
      `${name}/updateSymptom`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${treatmentPlanId}/symptoms/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function removeSymptom() {
    return createAsyncThunk(
      `${name}/removeSymptom`,
      async ({ treatmentPlanId, symptomId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${treatmentPlanId}/symptoms/${symptomId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<SymptomsState>) {
  const { getSymptoms, createSymptom, updateSymptom, removeSymptom } = extraActions;

  builder
    // getSymptoms()
    .addCase(getSymptoms.pending, (state: SymptomsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getSymptoms.fulfilled, (state: SymptomsState, action: any) => {
      state.loading = false;
      state.list = action.payload;
    })
    .addCase(getSymptoms.rejected, (state: SymptomsState, action: any) => {
      state.error = action.error;
      state.loading = false;
    })

    // createSymptom()
    .addCase(createSymptom.pending, (state: SymptomsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createSymptom.fulfilled,
      (state: SymptomsState, _action: PayloadAction<ITreatmentPlanSymptom>) => {
        state.loading = false;
      },
    )
    .addCase(createSymptom.rejected, (state: SymptomsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // updateSymptom()
    .addCase(updateSymptom.pending, (state: SymptomsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updateSymptom.fulfilled,
      (state: SymptomsState, _action: PayloadAction<ITreatmentPlanSymptom>) => {
        state.loading = false;
      },
    )
    .addCase(updateSymptom.rejected, (state: SymptomsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // removeSymptom()
    .addCase(removeSymptom.pending, (state: SymptomsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(removeSymptom.fulfilled, (state: SymptomsState, _action: any) => {
      state.loading = false;
    })
    .addCase(removeSymptom.rejected, (state: SymptomsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    });
}

export const symptomActions = { ...slice.actions, ...extraActions };
export const symptomReducer = slice.reducer;
